import Cookies from "js-cookie";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import { savePropertyAccessCities } from "../contexts/UserProfileContext";

// Visitors who arrived via the public Verify Listing ad landing page land on
// /verify-listing instead of /dashboard after logging in. Flag is cleared
// once consumed so a later, normal login isn't affected.
export const postLoginDestination = () => {
  if (localStorage.getItem("toolOnlyEntry") === "true") {
    localStorage.removeItem("toolOnlyEntry");
    return "/verify-listing";
  }
  return "/dashboard";
};

/**
 * Saves a session the backend issued ({ token, user, paymentHistory, ... }) the
 * same way for every entry point: the login OTP and the auto-login that
 * follows a verified signup. Callers navigate afterwards.
 *
 * `fetchUserProfile` is only needed for subusers whose plan expiry is not in
 * the session payload.
 */
export const saveLoginSession = async (session, { fetchUserProfile } = {}) => {
  const user = session.user;

  // Save token and user details
  Cookies.set("token", session.token, { expires: 365 });
  Cookies.set("userId", user._id, { expires: 365 });
  Cookies.set("bid", user.bid, { expires: 365 }); // Save broker ID (bid) in cookies
  Cookies.set("isPremium", user.isPremium || (session.adminExpiryInfo?.expiryDate ? "1" : "0"), {
    expires: 365,
  });
  Cookies.set("name", user.name, { expires: 365 });
  Cookies.set("number", user.number, { expires: 365 });
  Cookies.set("companyName", user.companyName || "", {
    expires: 365,
  });

  // Save plan name for demo account detection
  const planName =
    user?.planName ||
    user?.plan ||
    session.DemoAccount?.[0]?.PlanName ||
    session.DemoAccount?.[0]?.planName ||
    "";
  if (planName) {
    Cookies.set("planName", planName, { expires: 365 });
    Cookies.set("plan", planName, { expires: 365 });
  }

  // Cities this user is allowed to list in - drives the City dropdowns on
  // the property / requirement forms. Refreshed again by the profile fetch.
  savePropertyAccessCities(session);

  // Set userHierarchy to differentiate between main and subusers
  const hierarchy = user.parentUserId ? "subuser" : "main";
  Cookies.set("userHierarchy", hierarchy, { expires: 365 });

  // Correctly handle planExpiry for both main and subusers
  let planExpiry = session.paymentHistory?.[0]?.expiredDate ||
    session.paymentHistory?.[0]?.expiredOn ||
    session.adminExpiryInfo?.expiryDate ||
    session.adminExpiryInfo?.expiredDate ||
    user?.activePlanDetails?.expiredOn ||
    user?.activePlanDetails?.expiredDate;

  // If still missing and it's a subuser, fetch full profile details to guarantee planExpiry
  if (!planExpiry && user.parentUserId && typeof fetchUserProfile === "function") {
    try {
      const profileData = await fetchUserProfile(user._id);
      if (profileData) {
        planExpiry = profileData.adminExpiryInfo?.expiryDate ||
          profileData.adminExpiryInfo?.expiredDate ||
          profileData.paymentHistory?.[0]?.expiredDate;
      }
    } catch (e) {
      console.error("Error fetching admin expiry for subuser:", e);
    }
  }

  if (planExpiry) {
    Cookies.set("planExpiry", planExpiry, { expires: 30 });
  }

  const permission = user?.permission === true;
  Cookies.set("permission", permission ? "true" : "false", {
    expires: 365,
  });

  try {
    let currentDeviceId = null;

    // 🔹 Pick latest deviceId from backend
    if (user?.devices?.length > 0) {
      currentDeviceId = user.devices[user.devices.length - 1].deviceId;
    }

    // 🔹 If backend did not send, fallback to fingerprint
    if (!currentDeviceId) {
      const fp = await FingerprintJS.load();
      const result = await fp.get();
      currentDeviceId = result.visitorId;
    }

    // Save to cookie
    if (currentDeviceId) {
      Cookies.set("deviceId", currentDeviceId, { expires: 30 });
    }
  } catch (error) {
    console.error("Error saving deviceId:", error);
  }

  localStorage.setItem("justLoggedIn", "true");
};
